const SunSafelyAppPrivacyPolicy = () => {
  return (
    <div style={{ padding: 16, maxWidth: 760 }}>
      <h1>Privacy Policy for Sun Safely</h1>
      <p>Effective date: 2026-10-03</p>
      <p>
        Sun Safely ("the app") is a free app for iOS and Android made by devKey
        Software ("we", "us"). It shows the UV index where you are and estimates
        how long you can stay in the sun based on your skin type, sunscreen and
        surroundings. This policy explains what information the app uses, where
        it goes, and the choices you have.
      </p>
      <p>
        In short: we do not ask you to create an account, we do not show ads,
        we do not track you across apps or websites, and we do not sell your
        data. Your skin type and settings never leave your device. Usage
        statistics and crash reports are only sent if you agree to it, and you
        can change that at any time.
      </p>

      <h2>Information stored only on your device</h2>
      <p>
        The following is saved locally on your phone so the app works the next
        time you open it. It is never sent to us or to anyone else:
      </p>
      <ul>
        <li>Your chosen skin type (Fitzpatrick type 1–6)</li>
        <li>
          Your exposure settings, such as SPF and surroundings (water, sand,
          snow)
        </li>
        <li>The state of the sun timer</li>
        <li>
          Your last known location, place name and UV forecast, so the app can
          show something while fresh data loads
        </li>
        <li>Your theme, language and usage data sharing choice</li>
        <li>
          Which days you opened the app (only the last few, kept so the app does
          not ask for a store review too early or too often)
        </li>
      </ul>
      <p>
        You can delete all of this at any time by uninstalling the app.
      </p>

      <h2>Location</h2>
      <p>
        If you allow it, the app uses your device's location while the app is
        open. It never uses your location in the background. The app asks for
        approximate accuracy, which is enough because the UV level does not
        change much within a few kilometres.
      </p>
      <p>Your location is used for two things:</p>
      <ul>
        <li>
          <strong>Getting the UV forecast.</strong> Your coordinates are sent to
          Apple's WeatherKit service, which returns the weather and UV forecast
          for that place. This happens on both iOS and Android. Apple handles
          this request under the{" "}
          <a
            href="https://www.apple.com/legal/privacy/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Apple Privacy Policy
          </a>
          .
        </li>
        <li>
          <strong>Showing your city's name.</strong> The coordinates are turned
          into a place name using your phone's built-in geocoding service
          (provided by Apple on iOS and by Google on Android).
        </li>
      </ul>
      <p>
        Your location is not sent to our servers, is not included in usage
        statistics or crash reports, and is not shared with anyone else. If
        you do not allow location access, the app cannot show the UV forecast
        for where you are. You can change this permission at any time in your
        phone's settings.
      </p>

      <h2>Weather service access</h2>
      <p>
        To reach Apple's weather service, the app first gets a short-lived
        access key from a small server function that we run on Google Firebase
        (Cloud Functions). This request contains no personal information or
        location. As with any internet request, the server can see your
        device's IP address, and Google may keep it in short-term technical
        logs. We do not use it to identify you.
      </p>

      <h2>Usage statistics and crash reports (only with your consent)</h2>
      <p>
        When you first open the app, it asks whether you want to share
        anonymous usage data. Nothing is collected unless you choose "Share
        usage data". You can change your choice at any time in the app under
        Settings → Share usage data. Turning it off stops all collection right
        away.
      </p>
      <p>If you agree, the app uses these Google Firebase services:</p>
      <ul>
        <li>
          <strong>Firebase Analytics</strong>: which screens are viewed and
          which features are used, for example when the sun timer is started,
          paused or stopped and how long it ran, or when a setting is changed.
          Firebase also records general device information such as the device
          model, operating system version, app version, language and an
          approximate country, along with a random app instance identifier.
        </li>
        <li>
          <strong>Firebase Crashlytics</strong>: when the app crashes or hits
          an error, a report with technical details (such as the error message,
          the code location, the device model and operating system version) and
          a random installation identifier.
        </li>
      </ul>
      <p>
        This data never includes your location, your skin type, your name,
        your email address or your device's advertising identifier (IDFA or
        Android advertising ID). It is not linked to your identity and is not
        used for advertising or tracking. We only use it to find and fix bugs
        and to understand which features are useful. Google processes this
        data on our behalf under the{" "}
        <a
          href="https://firebase.google.com/support/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Firebase privacy and security terms
        </a>{" "}
        and the{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Privacy Policy
        </a>
        . Crash reports are kept for 90 days. Analytics data is kept for
        the retention period set in Google Analytics and is then deleted
        automatically.
      </p>

      <h2>Notifications and lock screen timer</h2>
      <p>
        While the sun timer is running, the app shows its progress on the lock
        screen: as a Live Activity on iOS and as an ongoing notification on
        Android. These are created on your device by the app. We do not use
        push notifications, and no notification token is sent to us.
      </p>

      <h2>Legal basis (EU/EEA users)</h2>
      <p>
        We use your location to provide the UV forecast you ask for (the
        service you request). We collect usage statistics and crash reports
        only on the basis of your consent, which you can withdraw at any time
        in the app's settings.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on where you live, you may have the right to access, correct
        or delete personal data about you, to object to or limit how it is
        used, and to withdraw consent. Most data stays on your device, where
        you control it directly. Because the usage statistics and crash reports
        we receive are not linked to your identity, we usually cannot find the
        data that belongs to you, but you are welcome to contact us and we will
        help as far as we can. If you are in the EU/EEA, you can also complain
        to your local data protection authority. In Sweden this is the Swedish
        Authority for Privacy Protection (IMY).
      </p>

      <h2>Children</h2>
      <p>
        The app is not directed at children under 13, and we do not knowingly
        collect personal information from them. If you believe a child has
        given us personal information, please contact us and we will delete
        it.
      </p>

      <h2>Security</h2>
      <p>
        All communication between the app and online services uses encrypted
        connections (HTTPS). No method of transmission or storage is 100%
        secure, but we keep what we collect to a minimum.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        We may update this policy, for example if the app gets new features.
        The latest version is always published on this page, with the
        effective date at the top.
      </p>

      <h2>Contact</h2>
      <p>
        devKey Software
        <br />
        Email: <a href="mailto:henrik@devkey.se">henrik@devkey.se</a>
      </p>
    </div>
  );
};

export default SunSafelyAppPrivacyPolicy;
